import DeletePatientButton from '@/components/layout/link/linked-session-list/delete-patient-button';
import Loader from '@/components/ui/loader';
import { PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { filterAndSortSessions } from '@/helpers/sessionSortHelpers';
import { isSessionPatientLinkingReadOnly } from '@/hooks/integrations/isSessionPatientLinkingReadOnly';
import { useMutateDeleteSessionsByIds } from '@/hooks/mutation/useMutateDeleteSessionsByIds';
import { useMutateUnlinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateUnlinkSessionsToPatientProfile';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { Button } from '@heidi/design-system/button';
import { Separator } from '@heidi/design-system/separator';
import { TypographyCaptionBold } from '@heidi/design-system/typography';
import { type PatientProfile } from '@heidi/patients/contracts';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { PatientSource } from '@heidi/schema/ml-scribe';
import { useQueryLinkedSessionsByProfileId } from '@heidi/session/data';
import { Check, Trash2, Undo2, Unlink } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import LinkedSession from './linked-session';

export const hiddenClassName =
  'text-text-primary-invertopacity-0 transition-all duration-300 -z-10 group-hover:z-50 group-hover:ml-4 group-hover:rounded group-hover:bg-background-primary group-hover:p-2 group-hover:opacity-100 h-full flex items-center';

type LinkedSessionListProps = {
  patientProfile?: PatientProfile;
  onSessionsDeleted?: () => void;
  forcePatientLinkingReadOnly?: boolean;
};

const LinkedSessionList = ({
  patientProfile,
  onSessionsDeleted,
  forcePatientLinkingReadOnly = false,
}: LinkedSessionListProps) => {
  const [selectedLinkedSessions, setSelectedLinkedSessions] = useState<string[]>([]);
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const { mutate: unlinkSessionsToPatientProfile } =
    useMutateUnlinkSessionsToPatientProfile();
  const currentSessionId = useCurrentSessionId();
  const { mutateAsync: deleteSessions } = useMutateDeleteSessionsByIds();
  const { data: linkedSessionsData, isLoading: isLoadingLinkedSessions } =
    useQueryLinkedSessionsByProfileId(patientProfile?.id || '');
  const linkedSessions = linkedSessionsData?.sessions;
  const { checkRBACPermission } = useRBACPermissionCheck();
  const canDeleteLinkedSessions = checkRBACPermission(
    PERMISSION_SUBJECTS.TEAM_OTHERS_SESSION_DELETE,
    PERMISSION_SUBJECTS.SESSION_DELETE
  );

  const onLinkedSessionSelected = (value: string) => {
    setSelectedLinkedSessions((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      }
      return [...prev, value];
    });
  };

  const onSelectAllClick = () => {
    if (isSelectedAll) {
      setSelectedLinkedSessions([]);
      setIsSelectedAll(false);
    } else {
      const allSessions = [
        ...(linkedSessions?.map((session) => session.session_id) || []),
      ];
      setSelectedLinkedSessions(allSessions);
      setIsSelectedAll(true);
    }
  };

  const pastSessions = filterAndSortSessions(false, linkedSessions || []);

  const upcomingSessions = filterAndSortSessions(true, linkedSessions || []);

  const onDeleteSelectedClick = async () => {
    setSelectedLinkedSessions([]);
    await deleteSessions({ sessionIds: selectedLinkedSessions });
    onSessionsDeleted?.();
  };

  const isEHRPatientProfile = patientProfile?.source === PatientSource.EHR;
  const isPatientLinkingReadOnly = forcePatientLinkingReadOnly || isEHRPatientProfile;
  const hasSelectedIntegrationSessions = selectedLinkedSessions.some((sessionId) => {
    const selectedSession = linkedSessions?.find(
      (session) => session.session_id === sessionId
    );
    return isSessionPatientLinkingReadOnly({ ehr_appt_id: selectedSession?.ehr_appt_id });
  });
  const isUnlinkSelectedDisabled =
    isPatientLinkingReadOnly || hasSelectedIntegrationSessions;

  return (
    <div className="flex w-[220px] flex-col gap-1 px-2 py-2.5">
      <div className="flex max-h-[332px] w-full flex-col gap-0.5 overflow-y-auto">
        {upcomingSessions && upcomingSessions.length > 0 && (
          <>
            <TypographyCaptionBold className="px-2">
              <FormattedMessage
                defaultMessage="Upcoming sessions"
                id="QO4yrbJjeq"
                description="Heading above the patient’s upcoming linked sessions"
              />
            </TypographyCaptionBold>
            {upcomingSessions?.map((session) => (
              <LinkedSession
                patientProfile={session.patient_profile || undefined}
                key={session.session_id}
                session={session}
                isSelected={
                  selectedLinkedSessions &&
                  selectedLinkedSessions.includes(session.session_id)
                }
                onLinkedSessionSelected={onLinkedSessionSelected}
                isEHRPatientProfile={isEHRPatientProfile}
                isPatientLinkingReadOnly={isPatientLinkingReadOnly}
              />
            ))}
          </>
        )}

        {isLoadingLinkedSessions && (
          <div className="flex h-full items-center justify-center">
            <Loader className="text-text-secondary" size={16} />
          </div>
        )}

        {pastSessions && pastSessions.length > 0 && (
          <>
            <TypographyCaptionBold className="px-2">
              <FormattedMessage
                defaultMessage="Past sessions"
                id="gNg1QdxVwG"
                description="Heading above the patient’s past linked sessions"
              />
            </TypographyCaptionBold>
            {pastSessions?.map((session) => (
              <LinkedSession
                patientProfile={session.patient_profile || undefined}
                key={session.session_id}
                session={session}
                isSelected={
                  selectedLinkedSessions &&
                  selectedLinkedSessions.includes(session.session_id)
                }
                onLinkedSessionSelected={onLinkedSessionSelected}
                isEHRPatientProfile={isEHRPatientProfile}
                isPatientLinkingReadOnly={isPatientLinkingReadOnly}
              />
            ))}
          </>
        )}
      </div>

      {!isPatientLinkingReadOnly &&
        (selectedLinkedSessions.length > 0 ||
          (canDeleteLinkedSessions && patientProfile?.id)) && (
          <>
            <Separator />
            <div className="flex flex-col gap-1">
              {/* 
          Button: Select all / Deselect all
        */}
              {selectedLinkedSessions.length > 0 && (
                <>
                  <Button
                    disableAutomaticSvgSizing
                    variant={'outline'}
                    size="xs"
                    className="text-text-secondary"
                    onClick={() => {
                      onSelectAllClick();
                    }}
                  >
                    <TypographyCaptionBold className="flex w-full items-center justify-center gap-2">
                      <div>
                        {isSelectedAll ? <Undo2 size={12} /> : <Check size={12} />}
                      </div>
                      {isSelectedAll ? (
                        <FormattedMessage
                          defaultMessage="Deselect all"
                          id="wZhmpxPv21"
                          description="Action to deselect all linked sessions"
                        />
                      ) : (
                        <FormattedMessage
                          defaultMessage="Select all"
                          id="pOSyVhSNCQ"
                          description="Action to select all linked sessions"
                        />
                      )}
                    </TypographyCaptionBold>
                  </Button>
                  <Button
                    disableAutomaticSvgSizing
                    variant={'outline'}
                    size="xs"
                    className="text-text-secondary"
                    disabled={isUnlinkSelectedDisabled}
                    onClick={() => {
                      if (patientProfile?.id) {
                        unlinkSessionsToPatientProfile({
                          sessionIds: selectedLinkedSessions,
                          patientProfile: patientProfile,
                          sessionId: currentSessionId || undefined,
                        });
                      }
                    }}
                  >
                    <TypographyCaptionBold className="flex w-full items-center justify-center gap-2">
                      <div>
                        <Unlink size="12" />
                      </div>
                      <FormattedMessage
                        defaultMessage="{count, plural, one {Unlink selected (#)} other {Unlink selected (#)}}"
                        id="eiVTkI1l3o"
                        description="Action to unlink selected sessions from the patient, with the number selected"
                        values={{ count: selectedLinkedSessions.length }}
                      />
                    </TypographyCaptionBold>
                  </Button>
                  {canDeleteLinkedSessions && (
                    <Button
                      disableAutomaticSvgSizing
                      variant={'destructive_soft'}
                      size="xs"
                      onClick={() => {
                        void onDeleteSelectedClick();
                      }}
                    >
                      <TypographyCaptionBold className="flex w-full items-center justify-center gap-2">
                        <div>
                          <Trash2 size="12" />
                        </div>
                        <FormattedMessage
                          defaultMessage="{count, plural, one {Delete selected (#)} other {Delete selected (#)}}"
                          id="qHwZYKOj0T"
                          description="Action to delete selected sessions, with the number selected"
                          values={{ count: selectedLinkedSessions.length }}
                        />
                      </TypographyCaptionBold>
                    </Button>
                  )}
                </>
              )}
              {canDeleteLinkedSessions &&
                selectedLinkedSessions.length === 0 &&
                patientProfile?.id && (
                  <DeletePatientButton patientId={patientProfile?.id} isCaptionTypography>
                    <FormattedMessage
                      defaultMessage="Delete all patient sessions"
                      id="+Wq5pDBbMx"
                      description="Action to delete every session linked to the patient"
                    />
                  </DeletePatientButton>
                )}
            </div>
          </>
        )}
    </div>
  );
};

export default LinkedSessionList;
